/**
 * Transaction Token Hook
 * 交易令牌 Hook - 管理功能級別的交易令牌
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  requestTransactionToken,
  revokeTransactionToken,
  getTokenInfo,
  TransactionTokenResponse
} from '../services/transactionService';

export interface UseTransactionTokenResult {
  txnToken: string | null;
  permissions: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    print: boolean;
    file: boolean;
  } | null;
  loading: boolean;
  error: string | null;
  remainingSeconds: number;
  requestToken: () => Promise<void>;
  revokeToken: () => Promise<void>;
  refreshToken: () => Promise<void>;
  extendToken: () => Promise<void>;
  showExtendPrompt: boolean;
  handleExtendResponse: (extend: boolean) => Promise<void>;
}

/**
 * 使用交易令牌 Hook
 *
 * @param funcCode 功能代碼
 * @param autoRequest 是否自動申請令牌(預設 true)
 * @param autoRevoke 是否在元件卸載時自動撤銷令牌(預設 true)
 * @returns 令牌管理物件
 */
export const useTransactionToken = (
  funcCode: string,
  autoRequest: boolean = true,
  autoRevoke: boolean = true
): UseTransactionTokenResult => {
  const [txnToken, setTxnToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<TransactionTokenResponse['permissions'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showExtendPrompt, setShowExtendPrompt] = useState(false);

  const hasRequested = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const extendPromptShown = useRef(false);
  const extendTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 申請令牌 (v3.0: 直接使用登入時的 token)
  const requestToken = useCallback(async () => {
    if (!funcCode) {
      setError('功能代碼不能為空');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // v3.0: 直接從 localStorage 讀取登入時儲存的 token
      const storedToken = localStorage.getItem('txn_token');

      if (!storedToken) {
        throw new Error('未找到交易令牌,請重新登入');
      }

      // 設定 token (v3.0 token 包含所有功能權限)
      setTxnToken(storedToken);

      // v3.0: token 包含所有權限,這裡設定為全部允許
      // 實際權限由後端驗證
      setPermissions({
        create: true,
        read: true,
        update: true,
        delete: true,
        print: true,
        file: true
      });

      // 設定 30 分鐘有效期 (1800 秒)
      setRemainingSeconds(1800);

      console.log(`[useTransactionToken] 使用登入 token: ${funcCode}`);

      // 開始定期檢查令牌狀態
      startTokenCheck(storedToken);
    } catch (err: any) {
      const errorMsg = err.message || '讀取令牌失敗';
      setError(errorMsg);
      console.error('[useTransactionToken] 讀取令牌失敗:', err);
    } finally {
      setLoading(false);
    }
  }, [funcCode]);

  // 撤銷令牌
  const revokeToken = useCallback(async () => {
    if (!txnToken) return;

    try {
      await revokeTransactionToken(txnToken);
      setTxnToken(null);
      setPermissions(null);
      setRemainingSeconds(0);

      // 停止檢查
      stopTokenCheck();

      console.log(`[useTransactionToken] 撤銷令牌成功: ${funcCode}`);
    } catch (err: any) {
      console.error('[useTransactionToken] 撤銷令牌失敗:', err);
    }
  }, [txnToken, funcCode]);

  // 刷新令牌 (撤銷舊的,申請新的)
  const refreshToken = useCallback(async () => {
    await revokeToken();
    await requestToken();
  }, [revokeToken, requestToken]);

  // 延長令牌 (重新申請以延長有效期)
  const extendToken = useCallback(async () => {
    if (!funcCode) return;

    try {
      // 重新申請 token,後端會自動延長現有 token 的 TTL
      const response = await requestTransactionToken(funcCode);

      setTxnToken(response.txn_token);
      setPermissions(response.permissions);
      setRemainingSeconds(response.expires_in);
      setShowExtendPrompt(false);
      extendPromptShown.current = false;

      console.log(`[useTransactionToken] 延長令牌成功: ${funcCode}, 已延長 30 分鐘`);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || '延長令牌失敗';
      setError(errorMsg);
      console.error('[useTransactionToken] 延長令牌失敗:', err);
    }
  }, [funcCode]);

  // 處理使用者延長回應
  const handleExtendResponse = useCallback(async (extend: boolean) => {
    // 清除自動取消計時器
    if (extendTimeoutRef.current) {
      clearTimeout(extendTimeoutRef.current);
      extendTimeoutRef.current = null;
    }

    if (extend) {
      // 使用者選擇延長
      await extendToken();
    } else {
      // 使用者選擇不延長,撤銷 token
      await revokeToken();
      setShowExtendPrompt(false);
      console.log(`[useTransactionToken] 使用者選擇不延長交易時間,已取消交易: ${funcCode}`);
    }
  }, [extendToken, revokeToken, funcCode]);

  // 定期檢查令牌狀態
  const startTokenCheck = (token: string) => {
    stopTokenCheck();

    checkIntervalRef.current = setInterval(async () => {
      try {
        const info = await getTokenInfo(token);
        setRemainingSeconds(info.remaining_seconds);

        // 如果剩餘時間少於 3 分鐘且尚未顯示提示,詢問使用者是否延長
        if (info.remaining_seconds < 180 && info.remaining_seconds > 0 && !extendPromptShown.current) {
          extendPromptShown.current = true;
          setShowExtendPrompt(true);

          console.warn(`[useTransactionToken] 交易時限將抵達 (剩餘 ${Math.floor(info.remaining_seconds / 60)} 分鐘),詢問使用者是否延長`);

          // 設定 3 分鐘後自動取消交易 (如果使用者沒有回應)
          extendTimeoutRef.current = setTimeout(async () => {
            console.warn(`[useTransactionToken] 使用者未回應或時限已超過,自動取消交易: ${funcCode}`);
            await revokeToken();
            setShowExtendPrompt(false);
            extendPromptShown.current = false;
          }, info.remaining_seconds * 1000); // 剩餘時間後自動取消
        }

        // 如果已過期,清除狀態
        if (info.remaining_seconds <= 0) {
          setTxnToken(null);
          setPermissions(null);
          setRemainingSeconds(0);
          setShowExtendPrompt(false);
          extendPromptShown.current = false;
          stopTokenCheck();
        }
      } catch (err) {
        // Token 可能已過期或被撤銷
        setTxnToken(null);
        setPermissions(null);
        setRemainingSeconds(0);
        setShowExtendPrompt(false);
        extendPromptShown.current = false;
        stopTokenCheck();
      }
    }, 30000); // 每 30 秒檢查一次
  };

  // 停止檢查
  const stopTokenCheck = () => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    if (extendTimeoutRef.current) {
      clearTimeout(extendTimeoutRef.current);
      extendTimeoutRef.current = null;
    }
  };

  // 自動申請令牌
  useEffect(() => {
    if (autoRequest && !hasRequested.current) {
      hasRequested.current = true;
      requestToken();
    }
  }, [autoRequest, requestToken]);

  // 元件卸載時停止 token 監控 (v3.0: 不撤銷 token，session-level token 由登出統一管理)
  useEffect(() => {
    return () => {
      stopTokenCheck();
      // v3.0: 一個 session 一個 token，元件卸載時不撤銷 token
      // token 會在登出時或 30 分鐘無操作後自動過期
      if (txnToken) {
        console.log(`[useTransactionToken] 元件卸載，停止 token 監控: ${funcCode}`);
      }
    };
  }, [txnToken, funcCode]);

  return {
    txnToken,
    permissions,
    loading,
    error,
    remainingSeconds,
    requestToken,
    revokeToken,
    refreshToken,
    extendToken,
    showExtendPrompt,
    handleExtendResponse
  };
};
